import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientIllustrationKeys } from '@/api/waterlily';
import { clientIllustrationsApi } from '@/features/shop-insurance/api/client-illustrations-api';
import {
  getIllustrationDisplayStatus,
  IllustrationDisplayStatus,
} from '@/features/shop-insurance/lib/illustration-display-status';

const GENERATING_POLL_INTERVAL_MS = 5_000;

export function useClientIllustrations(clientId: string | undefined) {
  const query = useQuery({
    queryKey: clientIllustrationKeys.list({ clientId }),
    queryFn: () => clientIllustrationsApi.getIllustrations(clientId!),
    enabled: !!clientId,
    refetchInterval: q => {
      const illustrations = q.state.data?.illustrations;
      if (!illustrations?.length) return false;
      const hasGenerating = illustrations.some(
        i => getIllustrationDisplayStatus(i) === 'generating',
      );
      return hasGenerating ? GENERATING_POLL_INTERVAL_MS : false;
    },
  });

  const illustrations = query.data?.illustrations ?? [];

  return {
    illustrations,
    count: illustrations.length,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export type IllustrationStatusFilter = IllustrationDisplayStatus | 'all';

export function useIllustrationsPanel(clientId: string | undefined) {
  const { illustrations, count, isLoading, isError } = useClientIllustrations(clientId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<IllustrationStatusFilter>('all');

  const filteredIllustrations = useMemo(() => {
    const term = search.trim().toLowerCase();
    return illustrations.filter(illustration => {
      if (
        statusFilter !== 'all' &&
        getIllustrationDisplayStatus(illustration) !== statusFilter
      ) {
        return false;
      }
      if (!term) return true;
      const haystack = [
        illustration.quote?.productName,
        illustration.quote?.carrierName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [illustrations, search, statusFilter]);

  return {
    illustrations: filteredIllustrations,
    totalCount: count,
    isLoading,
    isError,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
  };
}
